import { useQuery } from '@tanstack/react-query'
import { ROLES, ROLE_DESCRIPTION, can } from '@kronos/domain'
import type { Role } from '@kronos/domain'
import { repository } from '../data'
import type { TeamMember } from '../data'
import { EmptyState, Panel, Section } from './primitives'
import { ROLE_LABEL, RoleChecks } from './RoleChecks'

/**
 * The team roster (§11.1, Settings): everyone who can sign in, and what each of
 * them may do.
 *
 * **Roles are a set, not a rank**, so this is five checkboxes per person rather
 * than one dropdown. That is the whole point of the screen: at seven people the
 * same person buys the actuators and builds the arm, and the honest way to say
 * so is to tick two boxes — not to invent a "senior technician" that means
 * "purchaser and builder" to whoever set it and something else to everyone
 * else.
 *
 * Every tick is written immediately. There is no Save button because there is
 * no draft: a half-changed roster is not a state anybody wants to be holding
 * when they get called away, and a change that is refused says so on the row it
 * was refused on.
 */
export function TeamView({ roles }: { roles: readonly Role[] }): JSX.Element {
  const mayAdminister = can(roles, 'administer')
  const team = useQuery({
    queryKey: ['team'],
    queryFn: () => repository.team(),
    enabled: mayAdminister,
  })

  if (!mayAdminister) return <NotAManager roles={roles} />

  const members = team.data ?? []
  const active = members.filter((m) => m.deactivatedAt === null)
  const gone = members.filter((m) => m.deactivatedAt !== null)

  return (
    <div className="page">
      <Panel padded>
        <dl className="role-legend">
          {ROLES.map((r) => (
            <div key={r}>
              <dt>{ROLE_LABEL[r]}</dt>
              <dd>{ROLE_DESCRIPTION[r]}</dd>
            </div>
          ))}
        </dl>
      </Panel>

      {team.isError ? <p className="critical">Could not load the team.</p> : null}
      {team.isLoading ? <p className="muted">Loading…</p> : null}

      <Section label={`Team · ${active.length}`}>
        <Panel>
          <div className="row-list">
            {active.map((m) => (
              <MemberRow key={m.id} member={m} />
            ))}
          </div>
        </Panel>
      </Section>

      {gone.length > 0 ? (
        <Section label="Deactivated — can’t sign in">
          <Panel>
            <div className="row-list">
              {gone.map((m) => (
                <MemberRow key={m.id} member={m} />
              ))}
            </div>
          </Panel>
        </Section>
      ) : null}
    </div>
  )
}

function MemberRow({ member }: { member: TeamMember }): JSX.Element {
  const inactive = member.deactivatedAt !== null
  return (
    <div className="row-item member-row" style={{ opacity: inactive ? 0.6 : 1 }}>
      <span className="row" style={{ gap: 10, minWidth: 0 }}>
        {member.avatarUrl ? (
          <img className="avatar" src={member.avatarUrl} alt="" width={32} height={32} />
        ) : (
          <span className="avatar avatar--blank" aria-hidden="true">
            {member.name.slice(0, 1).toUpperCase()}
          </span>
        )}
        <span className="row-item__main">
          <span className="row-item__title">{member.name}</span>
          <span className="row-item__meta">{member.email}</span>
        </span>
      </span>
      <RoleChecks member={member} />
    </div>
  )
}

function NotAManager({ roles }: { roles: readonly Role[] }): JSX.Element {
  return (
    <Panel>
      <EmptyState title="Only an admin changes who does what">
        Your roles today: {ROLES.filter((r) => roles.includes(r)).map((r) => ROLE_LABEL[r]).join(', ')}.
      </EmptyState>
    </Panel>
  )
}
